import React from 'react';
import { FaTimes, FaCheck } from 'react-icons/fa';

const Step3SubmittedDataModal = ({ 
  isOpen, 
  onClose, 
  customTable,
  onMarkCompleted
}) => {
  if (!isOpen || !customTable) return null;

  const { columns, rows, customRows, userSubmittedData, userSubmittedAt } = customTable;
  
  // Standardize the labels from either the new customRows structure or the legacy rows structure
  const tableRows = customRows || rows?.map(r => ({ 
    adminValue: r[0], 
    isMandatory: columns?.[0]?.isMandatory ?? true 
  })) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">Step 3 - Client Submitted Data</h2>
            {userSubmittedAt && (
              <p className="text-sm text-blue-100 mt-1">
                Submitted on: {new Date(userSubmittedAt).toLocaleString()}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50/30">
          {/* Submission Details */}
          {userSubmittedData && userSubmittedData.length > 0 ? (
            <div className="animate-fade-in-up">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <div className="w-1.5 h-6 bg-blue-600 rounded-full"></div>
                  Submission Details
                </h3>
              </div>
              
              <div className="overflow-hidden border border-gray-200 rounded-2xl shadow-xl bg-white">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider w-[45%]">
                        Information Required
                      </th>
                      <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider w-[55%]">
                        Client Remarks
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {userSubmittedData.map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-blue-50/30 transition-colors duration-200">
                        <td className="px-6 py-4 align-top border-r border-gray-50">
                          <div className="flex gap-4">
                            <span className="text-blue-200 font-black text-sm tabular-nums mt-0.5">
                              {(rowIndex + 1).toString().padStart(2, '0')}
                            </span>
                            <p className="text-gray-700 text-sm font-semibold leading-relaxed whitespace-pre-wrap">
                              {tableRows[rowIndex]?.adminValue || row[0] || `-`}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="bg-blue-50/50 border border-blue-100/50 rounded-xl p-4 text-blue-900 font-bold text-sm leading-relaxed shadow-sm min-h-[60px]">
                            {/* row[1] is the user remark */}
                            {row[1] || <span className="text-gray-400 italic font-medium">No remarks provided</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center animate-pulse">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheck className="text-amber-500 text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-amber-900 mb-2">Pending Submission</h3>
              <p className="text-amber-700 max-w-sm mx-auto font-medium">
                The client has not yet submitted their remarks for this Step 3 form.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          {userSubmittedData && userSubmittedData.length > 0 && (
            <button
              onClick={onMarkCompleted}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FaCheck size={16} />
              Mark Step 3 as Completed
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step3SubmittedDataModal;
